import { useEffect, useState, useCallback } from "react";
import { Search, Loader2, Phone, Car, Calendar, Download, X, Trash2, FileText, AlertTriangle, Star } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = [
  { key: "nouveau", label: "Nouveau", color: "#3B82F6" },
  { key: "a_rappeler", label: "À rappeler", color: "#F59E0B" },
  { key: "en_cours", label: "En cours", color: "#8B5CF6" },
  { key: "resolu", label: "Résolu", color: "#10B981" },
  { key: "archive", label: "Archivé", color: "#6B7280" },
];
const statusMap = Object.fromEntries(STATUSES.map((s) => [s.key, s]));

const SENTIMENTS = [
  { key: "negatif", label: "Négatif", color: "#EF4444" },
  { key: "neutre", label: "Neutre", color: "#A0A5B5" },
  { key: "positif", label: "Positif", color: "#00E676" },
];
const sentimentMap = Object.fromEntries(SENTIMENTS.map((s) => [s.key, s]));

const SentimentBadge = ({ sentiment }) => {
  if (!sentiment) return null;
  const s = sentimentMap[sentiment];
  if (!s) return null;
  return (
    <span className="text-xs font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1" style={{ background: `${s.color}22`, color: s.color }}>
      {sentiment === "negatif" && <AlertTriangle className="w-3 h-3" />} {s.label}
    </span>
  );
};

const Badge = ({ status }) => {
  const s = statusMap[status] || STATUSES[0];
  return (
    <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: `${s.color}22`, color: s.color }}>
      {s.label}
    </span>
  );
};

export default function Inbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sentiment, setSentiment] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filter !== "all") params.status = filter;
    if (sentiment !== "all") params.sentiment = sentiment;
    if (search) params.search = search;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/admin/feedback", { params }).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filter, sentiment, search, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (item) => { setSelected(item); setNote(item.internal_note || ""); };

  const changeStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/feedback/${id}`, { status });
      setItems((arr) => arr.map((i) => (i.id === id ? data : i)));
      if (selected?.id === id) setSelected(data);
      toast.success("Statut mis à jour");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveNote = async () => {
    try {
      const { data } = await api.patch(`/admin/feedback/${selected.id}`, { internal_note: note });
      setItems((arr) => arr.map((i) => (i.id === data.id ? data : i)));
      setSelected(data);
      toast.success("Note enregistrée");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/feedback/${id}`);
      setItems((arr) => arr.filter((i) => i.id !== id));
      setSelected(null);
      toast.success("Retour supprimé");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportData = (fmt) => {
    window.open(`${API}/admin/feedback/export?fmt=${fmt}`, "_blank");
  };

  const inputCls = "bg-[#0a0b0e] border border-[#262a33] rounded-xl px-3 py-2 text-white text-sm outline-none focus:ring-2 focus:ring-[#00e676]";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Retours clients</h1>
          <p className="text-[#64697a] mt-1">Boîte de réception privée</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportData("csv")} data-testid="export-csv-btn" className="flex items-center gap-2 bg-[#2a2d35] hover:bg-[#363a45] text-white text-sm font-medium rounded-xl px-4 py-2 transition-colors">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button onClick={() => exportData("pdf")} data-testid="export-pdf-btn" className="flex items-center gap-2 bg-[#2a2d35] hover:bg-[#363a45] text-white text-sm font-medium rounded-xl px-4 py-2 transition-colors">
            <FileText className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      {/* filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-[#64697a] absolute left-3 top-1/2 -translate-y-1/2" />
          <input placeholder="Rechercher un client, véhicule..." value={search} onChange={(e) => setSearch(e.target.value)} className={inputCls + " w-full pl-9"} data-testid="search-input" />
        </div>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={inputCls} data-testid="date-from" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={inputCls} data-testid="date-to" />
      </div>

      <div className="flex flex-wrap gap-2">
        {[{ key: "all", label: "Tous" }, ...STATUSES].map((s) => (
          <button key={s.key} onClick={() => setFilter(s.key)} data-testid={`filter-${s.key}`}
            className={`text-sm font-medium px-3.5 py-1.5 rounded-full transition-colors ${filter === s.key ? "bg-[#00e676] text-[#04120a]" : "bg-[#161920] text-[#a0a5b5] hover:bg-[#1e222b]"}`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-[#64697a] flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Filtre auto :</span>
        {[{ key: "all", label: "Tous sentiments" }, ...SENTIMENTS].map((s) => (
          <button key={s.key} onClick={() => setSentiment(s.key)} data-testid={`sentiment-filter-${s.key}`}
            className={`text-sm font-medium px-3.5 py-1.5 rounded-full transition-colors ${sentiment === s.key ? "bg-white text-[#0a0b0e]" : "bg-[#161920] text-[#a0a5b5] hover:bg-[#1e222b]"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* list */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-[#00e676] animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-[#64697a]" data-testid="empty-inbox">Aucun retour pour ces critères.</div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <button key={it.id} onClick={() => openDetail(it)} data-testid={`feedback-item-${it.id}`}
              className="w-full text-left bg-[#161920] border border-[#262a33] hover:border-[#363a45] rounded-xl p-4 transition-colors">
              <div className="flex items-center justify-between mb-2 gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-semibold truncate">{it.first_name}</span>
                  <Badge status={it.status} />
                  <SentimentBadge sentiment={it.sentiment} />
                </div>
                <span className="text-xs text-[#64697a] whitespace-nowrap">{(it.created_at || "").slice(0, 10)}</span>
              </div>
              <p className="text-sm text-[#a0a5b5] line-clamp-2">{it.comment}</p>
              <div className="flex flex-wrap gap-3 mt-2 text-xs text-[#64697a]">
                <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {it.phone}</span>
                {it.vehicle && <span className="flex items-center gap-1"><Car className="w-3 h-3" /> {it.vehicle}</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setSelected(null)}>
          <div className="bg-[#161920] border border-[#262a33] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="feedback-detail">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-display text-2xl font-bold">{selected.first_name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-[#64697a] text-sm">{(selected.created_at || "").replace("T", " ").slice(0, 16)}</p>
                  {selected.rating ? (
                    <span className="flex items-center gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-3.5 h-3.5 ${i < selected.rating ? "fill-[#00e676] text-[#00e676]" : "text-[#363a45]"}`} />
                      ))}
                    </span>
                  ) : null}
                  <SentimentBadge sentiment={selected.sentiment} />
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="p-1 text-[#64697a] hover:text-white" data-testid="close-detail"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <Info label="Téléphone" value={selected.phone} />
              <Info label="E-mail" value={selected.email || "—"} />
              <Info label="Véhicule" value={selected.vehicle || "—"} />
              <Info label="Prestation" value={selected.service || "—"} />
              <Info label="Objet" value={selected.subject || "—"} />
              <Info label="Recontact" value={selected.allow_recontact ? "Autorisé" : "Non"} />
            </div>

            <div className="bg-[#0a0b0e] border border-[#262a33] rounded-xl p-4 mb-4">
              <div className="text-xs text-[#64697a] mb-1">Commentaire</div>
              <p className="text-sm text-white whitespace-pre-line">{selected.comment}</p>
            </div>

            <div className="mb-4">
              <div className="text-xs text-[#64697a] mb-2">Statut</div>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button key={s.key} onClick={() => changeStatus(selected.id, s.key)} data-testid={`set-status-${s.key}`}
                    className="text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
                    style={selected.status === s.key
                      ? { background: s.color, color: "#fff" }
                      : { background: `${s.color}22`, color: s.color }}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <div className="text-xs text-[#64697a] mb-2">Note interne</div>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="internal-note"
                className="w-full bg-[#0a0b0e] border border-[#262a33] rounded-xl px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-[#00e676] min-h-[80px] resize-none" placeholder="Ajouter une note..." />
              <button onClick={saveNote} data-testid="save-note-btn" className="mt-2 bg-[#00e676] hover:bg-[#00c853] text-[#04120a] text-sm font-bold rounded-full px-4 py-2 transition-colors">
                Enregistrer la note
              </button>
            </div>

            <div className="flex justify-between items-center pt-3 border-t border-[#262a33]">
              <a href={`tel:${selected.phone}`} className="text-sm text-[#00e676] font-semibold flex items-center gap-1"><Phone className="w-4 h-4" /> Appeler</a>
              <button onClick={() => remove(selected.id)} data-testid="delete-feedback-btn" className="text-sm text-red-400 hover:text-red-300 flex items-center gap-1"><Trash2 className="w-4 h-4" /> Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Info = ({ label, value }) => (
  <div>
    <div className="text-xs text-[#64697a]">{label}</div>
    <div className="text-white truncate">{value}</div>
  </div>
);
